import React, { useEffect, useState } from 'react';
import { Table, Card, Button, Tag, Space, Modal, Input, message, Form, DatePicker, Select, Row, Col, Descriptions, Popconfirm, Upload } from 'antd';
import { Eye, Check, X as XIcon, Search, Upload as UploadIcon } from 'lucide-react';
import { getCommissionList, approveCommission, rejectCommission, settleDaily } from '@/api/Admin/commission';
// import { CommissionAdminAPI } from '@/api/Admin/type'; // Ensure this path is correct, might be implicit or different
import dayjs from 'dayjs';
import { PageHeader } from '@/components/PageHeader';
import { BreadcrumbItem } from '@/components/Breadcrumb';
import type { UploadFile } from 'antd/es/upload/interface';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;


interface CommissionData {
    id: number;
    user_id: number;
    source_user_id: number;
    gross_amount: number;
    tax_fee: number;
    net_amount: number;
    status: string;
    updated_at: string;
    created_at: string;
    currency: string;
    description: string;
    payable_at: string | null;
    transaction_id: number | null;
    type: string;
    user_info: {
        id: number;
        fullname: string;
        email: string;
        avatar: string | null;
        avatar_url: string | null;
        phone: string | null;
    }
    user_package_id: number;
}


const CommissionPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<CommissionData[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    // Filters
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | undefined>([
        dayjs().startOf('month'),
        dayjs().add(1, 'month').endOf('month')
    ]);
    const [userIdFilter, setUserIdFilter] = useState<number | undefined>(undefined);
    const [sourceUserIdFilter, setSourceUserIdFilter] = useState<number | undefined>(undefined);
    const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);

    // Modal State
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedCommission, setSelectedCommission] = useState<CommissionData | null>(null);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Approve Modal State
    const [approveModalVisible, setApproveModalVisible] = useState(false);
    const [approveFileList, setApproveFileList] = useState<UploadFile[]>([]);

    // Settle Modal State
    const [settleModalVisible, setSettleModalVisible] = useState(false);
    const [settleDate, setSettleDate] = useState<dayjs.Dayjs | null>(dayjs());
    const [settleLoading, setSettleLoading] = useState(false);

    async function fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    }

    const fetchData = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const startDate = dateRange ? dateRange[0].format('YYYY-MM-DD') : undefined;
            const endDate = dateRange ? dateRange[1].format('YYYY-MM-DD') : undefined;

            // Cast status to expected type if necessary, or let typescript infer
            const statusParam = statusFilter as 'waiting_tax_check' | 'pending' | 'paid' | undefined;

            const response = await getCommissionList(
                userIdFilter,
                startDate,
                endDate,
                statusParam,
                pageSize,
                page,
                sourceUserIdFilter,
                typeFilter
            );

            if (response && response.data) {
                setData(response.data.data);
                setPagination({
                    current: response.data.current_page,
                    pageSize: response.data.per_page,
                    total: response.data.total
                });
            }
        } catch (error) {
            console.error(error);
            // message.error('Failed to load commissions'); // Already handled in API wrapper usually?
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, statusFilter, dateRange, userIdFilter, sourceUserIdFilter, typeFilter]); // Refetch when filters change? Or trigger manually?
    // Often better to trigger manually for filters, but for simple use case:

    const handleSearch = () => {
        setPagination({ ...pagination, current: 1 });
        fetchData(1, pagination.pageSize);
    };

    const handleTableChange = (newPagination: any) => {
        fetchData(newPagination.current, newPagination.pageSize);
    };

    const openApproveModal = (record: CommissionData) => {
        setSelectedCommission(record);
        setApproveFileList([]);
        setApproveModalVisible(true);
    };

    const handleApproveConfirm = async () => {
        if (!selectedCommission) return;
        
        if (approveFileList.length === 0) {
            message.error('Please upload a transaction image');
            return;
        }

        const fileEntry = approveFileList[0];
        const file = (fileEntry.originFileObj || fileEntry) as File;

        setActionLoading(true);
        try {
            await approveCommission(selectedCommission.id, file);
            message.success('Commission approved successfully');
            setApproveModalVisible(false);
            setApproveFileList([]);
            fetchData(pagination.current, pagination.pageSize);
        } catch (error) {
            message.error('Failed to approve');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedCommission) return;
        if (!rejectReason.trim()) {
            message.error('Please enter a reason');
            return;
        }
        setActionLoading(true);
        try {
            await rejectCommission(selectedCommission.id, rejectReason);
            message.success('Commission rejected successfully');
            setRejectModalVisible(false);
            setRejectReason('');
            fetchData(pagination.current, pagination.pageSize);
        } catch (error) {
            message.error('Failed to reject');
        } finally {
            setActionLoading(false);
        }
    };

    const openRejectModal = (record: CommissionData) => {
        setSelectedCommission(record);
        setRejectReason('');
        setRejectModalVisible(true);
    };

    const openDetailModal = (record: CommissionData) => {
        setSelectedCommission(record);
        setDetailModalVisible(true);
    };

    const handleSettleDaily = async () => {
        if (!settleDate) {
            message.warning('Please select a date');
            return;
        }
        setSettleLoading(true);
        try {
            await settleDaily(settleDate.format('YYYY-MM-DD'));
            setSettleModalVisible(false);
        } catch (error) {
            console.error(error);
        } finally {
            setSettleLoading(false);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'User',
            dataIndex: 'user_info',
            key: 'user_info',
            width: 200,
            render: (user_info: CommissionData['user_info']) => (
                <div className="flex items-center space-x-2">
                    {user_info.avatar_url ? (
                        <img
                            src={user_info.avatar_url}
                            alt={user_info.fullname}
                            className="w-6 h-6 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs text-white">
                            {user_info.fullname.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <span>{user_info.fullname}</span>
                </div>
            )
        },
        {
            title: 'Gross Amount',
            dataIndex: 'gross_amount',
            key: 'gross_amount',
            render: (amount: number) => amount.toLocaleString(),
        },
        {
            title: 'Tax Fee',
            dataIndex: 'tax_fee',
            key: 'tax_fee',
            render: (amount: number) => amount.toLocaleString(),
        },
        {
            title: 'Net Amount',
            dataIndex: 'net_amount',
            key: 'net_amount',
            render: (amount: number) => amount.toLocaleString(),
        },
        {
            title: 'Currency',
            dataIndex: 'currency',
            key: 'currency',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                let color = 'default';
                if (status === 'paid') color = 'green';
                if (status === 'pending') color = 'orange';
                if (status === 'rejected') color = 'red';
                if (status === 'waiting_tax_check') color = 'blue';
                return <Tag color={color}>{status.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Payable At',
            dataIndex: 'payable_at',
            key: 'payable_at',
            render: (date: string | null) => date ? (
                <div>
                    <span>{dayjs(date).format('YYYY-MM-DD')}</span><br />
                    <span className="text-gray-500 text-sm">{dayjs(date).format('HH:mm:ss')}</span>
                </div>
            ) : 'N/A',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: CommissionData) => (
                <Space size="small">
                    <Button
                        icon={<Eye size={16} />}
                        size="small"
                        onClick={() => openDetailModal(record)}
                        title="View Details"
                    />
                    {record.status !== 'paid' && record.status !== 'rejected' && (
                        <>
                            <Button
                                icon={<Check size={16} />}
                                size="small"
                                type="primary"
                                className="bg-green-600 hover:bg-green-700"
                                title="Approve"
                                onClick={() => openApproveModal(record)}
                            />
                            <Button
                                icon={<XIcon size={16} />}
                                size="small"
                                danger
                                onClick={() => openRejectModal(record)}
                                title="Reject"
                            />
                        </>
                    )}
                </Space>
            )
        }
    ];

    const breadcrumbs: BreadcrumbItem[] = [
        { label: 'Admin', path: '/admin/dashboard' },
        { label: 'Commissions' },
    ];

    return (
        <div className="p-6">
            <PageHeader title="Commission Management" breadcrumbs={breadcrumbs} />

            <div className="flex justify-end mb-4">
                <Button type="primary" onClick={() => setSettleModalVisible(true)}>
                    Tất Toán
                </Button>
            </div>

            <Card className="mb-6">
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={8} md={4}>
                        <Input
                            placeholder="User ID"
                            type="number"
                            value={userIdFilter}
                            onChange={e => setUserIdFilter(e.target.value ? Number(e.target.value) : undefined)}
                            onPressEnter={handleSearch}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={8} md={4}>
                        <Input
                            placeholder="Source User ID"
                            type="number"
                            value={sourceUserIdFilter}
                            onChange={e => setSourceUserIdFilter(e.target.value ? Number(e.target.value) : undefined)}
                            onPressEnter={handleSearch}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={8} md={4}>
                        <Input
                            placeholder="Type"
                            value={typeFilter}
                            onChange={e => setTypeFilter(e.target.value)}
                            onPressEnter={handleSearch}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={8} md={4}>
                        <Select
                            className="w-full"
                            placeholder="Status"
                            allowClear
                            onChange={val => setStatusFilter(val)}
                        >
                            <Option value="pending">Pending</Option>
                            <Option value="waiting_tax_check">Waiting Tax Check</Option>
                            <Option value="paid">Paid</Option>
                            <Option value="rejected">Rejected</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={8} md={8}>
                        <RangePicker
                            className="w-full"
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates as any)}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={4}>
                        <Button type="primary" icon={<Search size={16} />} onClick={handleSearch} block>
                            Filter
                        </Button>
                    </Col>
                </Row>
            </Card>

            <Card>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                    }}
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{ x: 1000 }}
                />
            </Card>

            {/* Detail Modal */}
            <Modal
                title="Commission Details"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={700}
            >
                {selectedCommission && (
                    <Descriptions bordered column={1}>
                        <Descriptions.Item label="ID">{selectedCommission.id}</Descriptions.Item>
                        <Descriptions.Item label="User">{selectedCommission.user_info.fullname}</Descriptions.Item>
                        <Descriptions.Item label="Source User ID">{selectedCommission.source_user_id}</Descriptions.Item>
                        <Descriptions.Item label="Gross Amount">{selectedCommission.gross_amount.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="Tax Fee">{selectedCommission.tax_fee.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="Net Amount">{selectedCommission.net_amount.toLocaleString()}</Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag>{selectedCommission.status.toUpperCase()}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Description">{selectedCommission.description}</Descriptions.Item>
                        <Descriptions.Item label="Created At">{dayjs(selectedCommission.created_at).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* Approve Modal */}
            <Modal
                title="Approve Commission"
                open={approveModalVisible}
                onOk={handleApproveConfirm}
                onCancel={() => setApproveModalVisible(false)}
                confirmLoading={actionLoading}
                okText="Approve"
            >
                <p className="mb-4">Please upload the transaction proof image to approve this commission.</p>
                <Upload
                    fileList={approveFileList}
                    onRemove={() => setApproveFileList([])}
                    beforeUpload={(file) => {
                        setApproveFileList([file]);
                        return false;
                    }}
                    maxCount={1}
                    listType="picture"
                    accept="image/*"
                >
                    <Button icon={<UploadIcon size={16} />}>Select Transaction Image</Button>
                </Upload>
            </Modal>

            {/* Settle Daily Modal */}
            <Modal
                title="Settle Daily"
                open={settleModalVisible}
                onOk={handleSettleDaily}
                onCancel={() => setSettleModalVisible(false)}
                confirmLoading={settleLoading}
                okText="Settle"
            >
                <p className="mb-4">Select date to settle commissions:</p>
                <DatePicker 
                    value={settleDate} 
                    onChange={setSettleDate} 
                    className="w-full"
                    allowClear={false}
                    format="DD/MM/YYYY"
                />
            </Modal>

            {/* Reject Modal */}
            <Modal
                title="Reject Commission"
                open={rejectModalVisible}
                onOk={handleReject}
                onCancel={() => setRejectModalVisible(false)}
                confirmLoading={actionLoading}
                okText="Reject"
                okButtonProps={{ danger: true }}
            >
                <p>Please provide a reason for rejection:</p>
                <TextArea
                    rows={4}
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="Reason..."
                />
            </Modal>
        </div>
    );
};

export default CommissionPage;
